import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";

import { TripsheetInputFilter } from "domain/models/tripsheet-input-filter";
import { httpClient } from "infra/axios/axios-api";
import { useTripsheetsByFilter } from "presentation/hooks/use-query-tripsheet";
import { getTripsheetsByDigitalSign, getTripsheetsSigned } from "presentation/utils/exportUtils/timesheet-download";

// Componente interno com a lógica de negócio.
const TripsheetReportDownloaderInternal: React.FC<{
  date: Date;
  onComplete: () => void;
}> = ({ date, onComplete }) => {
  const toast = useRef<Toast>(null);
  const parsedYear = date.getFullYear();
  const parsedMonth = date.getMonth() + 1;

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");

  const { data: tripsheetData } = useTripsheetsByFilter({
    month: parsedMonth,
    year: parsedYear,
    status: "Assinado 1,Assinado 2",
    limit: 1000,
  } as TripsheetInputFilter);

  /**
   * Busca o blob de um PDF individual de tripsheet, aplicando a lógica de status.
   */
  const fetchTripsheetPdf = async (
    tripsheet: any
  ): Promise<{ blob: Blob; filename: string } | null> => {
    const employeeName = tripsheet.employee?.name || "Funcionário";
    const monthYear = `${String(parsedMonth).padStart(2, "0")}-${parsedYear}`;
    const sanitizedName =
      employeeName?.replace(/[^a-zA-Z0-9]/g, "_") || "funcionario";
    const filename = `mapa_quilometros_${sanitizedName}_${monthYear}_${tripsheet.id}.pdf`;

    try {
      console.log(tripsheet.id)
      const digitalSignResponse = await httpClient.get("/digitalSignApprover", {
        params: { referenceId: tripsheet.id, limit: 100, page: 1 },
      });
      console.log(digitalSignResponse)
      const digitalSignData = digitalSignResponse.data?.data?.filter(
        (item: any) => item.digitalSign?.documentType === "Tripsheet"
      );
      console.log(digitalSignData)
      if (!digitalSignData || digitalSignData.length === 0) {
        return null;
      }

      const docSignedByEmployee = digitalSignData.find(
        (item: any) => item.step === "Colaborador"
      );
      const docSignedByCompany = digitalSignData.find(
        (item: any) => item.step === "Nimber"
      );

      console.log(docSignedByEmployee, docSignedByCompany)
      const s3Path =
        docSignedByEmployee?.documentPath || docSignedByCompany?.documentPath;

      console.log(s3Path)
      if (!s3Path) {
        console.warn(
          `Path do documento não encontrado para tripsheet ${tripsheet.id}`
        );
        return null;
      }
      console.log(s3Path)
      const signedUrlResponse = await httpClient.get(`/assets/${s3Path}`);
      const signedUrl = signedUrlResponse.data?.url;
      if (!signedUrl) return null;

      const pdfResponse = await fetch(signedUrl);
      if (!pdfResponse.ok) return null;

      const blob = await pdfResponse.blob();
      if (blob.size === 0) return null;

      return { blob, filename };
    } catch (error) {
      console.error(`Erro ao baixar PDF para tripsheet ${tripsheet.id}:`, error);
      return null;
    }
  };

  /**
   * Orquestra o processo de download e compressão dos PDFs.
   */
  const handleDownload = async () => {
    setIsLoading(true);
    setProgress(0);
    setProgressMessage("A iniciar processo...");

    try {
      const onProgress = (current: number, total: number, message: string) => {
        setProgress((current / total) * 100);
        setProgressMessage(message);
      };

      const allTripsheets = tripsheetData?.data;

      if (!allTripsheets || allTripsheets.length === 0) {
        throw new Error(
          `Nenhum mapa de KM encontrado para ${parsedMonth}/${parsedYear}`
        );
      }

      onProgress(0, 100, `A preparar ${allTripsheets.length} ficheiros...`);

      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      let successCount = 0;
      const totalCount = allTripsheets.length;

      for (let i = 0; i < totalCount; i++) {
        const tripsheet = allTripsheets[i];
        const employeeName = tripsheet.employee?.name || "Funcionário";
        onProgress(i + 1, totalCount, `Processando ${employeeName}...`);

        const result = await fetchTripsheetPdf(tripsheet);
        if (result) {
          zip.file(result.filename, result.blob);
          successCount++;
        }
      }

      if (successCount === 0) {
        throw new Error("Nenhum PDF de Mapa de KM foi encontrado.");
      }

      onProgress(100, 100, "Gerando arquivo ZIP...");
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, "_");
      link.href = url;
      link.download = `mapas_km_todos_${parsedMonth}_${parsedYear}_${timestamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: `Download do ZIP concluído com ${successCount} de ${totalCount} ficheiros.`,
      });
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Erro desconhecido";
      toast.current?.show({
        severity: "error",
        summary: "Erro no Download",
        detail: `Não foi possível descarregar o ficheiro ZIP: ${msg}`,
        sticky: true,
      });
    } finally {
      setIsLoading(false);
      setProgress(0);
      setProgressMessage("");
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="default-grid header-spacing">
        <div className="default-2-cols">
          {tripsheetData?.data && tripsheetData.data.length > 0 ? (
            <p className="p-0 m-0">
              Foram encontrados {tripsheetData.data.length} Mapas de KM para o
              período {parsedMonth}/{parsedYear}.
            </p>
          ) : (
            <p className="p-0 m-0">
              0 Mapas de KM encontrados para o período {parsedMonth}/{parsedYear}.
            </p>
          )}
        </div>
        <div className="default-2-cols button-align">
          <Button
            label="Voltar"
            icon="pi pi-arrow-left"
            onClick={onComplete}
            disabled={isLoading}
            className="p-button-secondary"
          />

          <Button
            label="Descarregar"
            icon="pi pi-cloud-upload"
            onClick={handleDownload}
            loading={isLoading}
            disabled={!tripsheetData?.data || tripsheetData.data.length === 0}
            className="mr-3"
          />
        </div>
      </div>

      {isLoading && (
        <div className="mt-4">
          <ProgressBar value={progress.toFixed(0)} />
          {progressMessage && (
            <small className="p-text-secondary mt-1">{progressMessage}</small>
          )}
        </div>
      )}
    </div>
  );
};

interface TripsheetReportDownloaderProps {
  date: Date | null;
  onComplete: () => void;
}

const TripsheetReportDownloader: React.FC<TripsheetReportDownloaderProps> = ({
  date,
  onComplete,
}) => {
  if (!date) {
    return (
      <div className="flex flex-column align-items-center justify-content-center p-5 text-center">
        <i
          className="pi pi-calendar-times"
          style={{ fontSize: "3em", color: "var(--text-color-secondary)" }}
        ></i>
        <h3 className="mt-3 text-color-secondary">Mês de referência inválido</h3>
        <p className="text-color-secondary">
          Por favor, selecione um mês de referência válido no formulário
          anterior.
        </p>
        <Button
          label="Voltar"
          icon="pi pi-arrow-left"
          onClick={onComplete}
          className="mt-3 p-button-secondary"
        />
      </div>
    );
  }

  return (
    <TripsheetReportDownloaderInternal date={date} onComplete={onComplete} />
  );
};

export default TripsheetReportDownloader;